// Operator-facing "fire next challenge" picker. Used in Phase 3 to
// trigger transmissions manually until the scheduled engine lands in
// Phase 4. Phase 5 will gate this behind producer-only auth; for now
// any player can fire any test_mode_eligible challenge while we
// validate the pipeline.

import { useEffect, useMemo, useState } from 'react';
import { fireChallenge } from '../lib/api.js';
import { useTripJson } from '../hooks/data.js';
import { cleanWilmanText } from '../lib/wilmanText.js';

const TYPE_FRIENDLY = {
  photo: 'Photo',
  text: 'Text',
  photo_and_text: 'Photo + caption',
  multi_photo: 'Multi-photo',
  peer_attestation: 'Peer vote',
};

export default function ChallengePicker({ tripId, testMode, onFired }) {
  const bank = useTripJson(tripId, 'challenges.json');
  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const challenges = useMemo(() => {
    if (!bank.data?.challenges) return [];
    let list = bank.data.challenges;
    if (filter === 'test') list = list.filter((c) => c.test_mode_eligible);
    return [...list].sort((a, b) => a.title.localeCompare(b.title));
  }, [bank.data, filter]);

  useEffect(() => {
    if (!open) setError(null);
  }, [open]);

  async function fire(challengeId) {
    setError(null);
    setBusy(challengeId);
    try {
      const res = await fireChallenge({ tripId, challengeId, testMode });
      onFired?.(res);
      setOpen(false);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-md border border-border-tertiary bg-bg-secondary px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-fg-secondary transition hover:bg-bg-tertiary"
      >
        fire challenge
      </button>

      {open && (
        <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40" onClick={() => setOpen(false)}>
          <div
            className="card w-full max-w-screen-sm overflow-hidden rounded-b-none bg-bg-primary"
            style={{ paddingBottom: 'var(--safe-bottom)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-center justify-between border-b border-border-tertiary px-4 py-3">
              <h2 className="font-medium">Fire a challenge</h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="rounded-md p-1 text-fg-tertiary hover:bg-bg-tertiary hover:text-fg-primary"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </header>

            <div className="border-b border-border-tertiary px-4 py-2">
              <div className="flex gap-1">
                {['all', 'test'].map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setFilter(f)}
                    className={[
                      'rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-wider',
                      filter === f
                        ? 'border-action bg-action text-action-fg'
                        : 'border-border-tertiary bg-bg-secondary text-fg-secondary',
                    ].join(' ')}
                  >
                    {f === 'all' ? 'all' : 'test-mode eligible'}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <div className="mx-4 mt-2 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
                {error}
              </div>
            )}

            <ul className="max-h-[60vh] divide-y divide-border-tertiary overflow-y-auto">
              {challenges.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => fire(c.id)}
                    disabled={busy === c.id}
                    className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left transition hover:bg-bg-tertiary disabled:opacity-60"
                  >
                    <div className="min-w-0">
                      <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                        {c.category.replace(/_/g, ' ')} · {TYPE_FRIENDLY[c.submission_type] || c.submission_type}
                      </p>
                      <p className="font-medium">{c.title}</p>
                      <p className="line-clamp-1 font-serif text-xs italic text-fg-secondary">{cleanWilmanText(c.wilman_text)}</p>
                    </div>
                    <span className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                      {busy === c.id ? '…' : `${c.points_base}p`}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}
